import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ArrowRight, Home, TrendingUp, Settings, ChevronLeft } from "lucide-react";

const LIFE_HERO = "https://images.unsplash.com/photo-1770751188011-8ab6c81552bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const INVEST_HERO = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const THEMES = {
  first_home_buyer: {
    icon: Home, eyebrow: "AI Home Decision Engine", hero: LIFE_HERO,
    quote: "A home isn't chosen. It's discovered — around the life you already love.",
    title: ["Let's build your", "first home together."],
    sub: "A calm, guided conversation. We'll discover how you want to live, then design the home and the numbers around it — together, one step at a time.",
    button: "Begin the journey",
  },
  home_buyer: {
    icon: Home, eyebrow: "AI Home Decision Engine", hero: LIFE_HERO,
    quote: "A home isn't chosen. It's discovered — around the life you already love.",
    title: ["Let's build your", "life together."],
    sub: "A calm, guided conversation. We'll discover how you want to live, then design the home and the numbers around it — together, one step at a time.",
    button: "Begin the journey",
  },
  investor: {
    icon: TrendingUp, eyebrow: "AI Wealth Decision Engine", hero: INVEST_HERO,
    quote: "An investment home isn't chosen. It's planned — around the needs of your tenants.",
    title: ["Let's build", "wealth together."],
    sub: "A guided plan around yield, capital growth and tax. We'll shape the investment and the numbers around your goals — one step at a time.",
    button: "Begin the plan",
  },
};

export default function Welcome() {
  const { start, clientType, setClientType } = useJourney();
  const [consultant, setConsultant] = useState("");
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const theme = THEMES[clientType] || THEMES.home_buyer;
  const Icon = theme.icon;

  const begin = async () => {
    if (!consultant.trim() || !clientName.trim()) {
      toast.error("Add both names to begin the session.");
      return;
    }
    setLoading(true);
    try {
      await start(consultant.trim(), clientName.trim());
    } catch (e) {
      toast.error("Could not start session.");
      setLoading(false);
    }
  };

  return (
    <StageFrame testId="stage-welcome" className="flex flex-col lg:flex-row">
      <button data-testid="admin-link" onClick={() => navigate("/admin")}
        className="hotspot fixed top-4 right-5 z-40 flex items-center gap-2 text-xs text-white/80 hover:text-white transition-colors">
        <Settings size={14} /> Admin
      </button>

      <div className="relative lg:w-[52%] h-[40vh] lg:h-screen overflow-hidden">
        <motion.img key={theme.hero} src={theme.hero} alt="Home"
          initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <p className="font-serif text-white text-3xl md:text-4xl font-light leading-tight max-w-md">
            "{theme.quote}"
          </p>
        </div>
      </div>

      <div className="lg:w-[48%] flex items-center px-8 md:px-16 py-16">
        <div className="w-full max-w-md">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}>
            <button data-testid="welcome-change-type" onClick={() => setClientType(null)}
              className="hotspot flex items-center gap-1.5 text-xs text-mutedText hover:text-forest mb-6 transition-colors">
              <ChevronLeft size={14} /> Choose a different journey
            </button>

            <div className="flex items-center gap-2 mb-6">
              <Icon size={18} className="text-brand" />
              <Eyebrow>{theme.eyebrow}</Eyebrow>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-light leading-[0.95] tracking-tight text-forest mb-5">
              {theme.title[0]}<br />{theme.title[1]}
            </h1>
            <p className="text-mutedText text-base leading-relaxed mb-10">{theme.sub}</p>

            <div className="space-y-4">
              <div>
                <label className="label-eyebrow text-mutedText">Consultant</label>
                <input data-testid="welcome-consultant-input" value={consultant} onChange={(e) => setConsultant(e.target.value)}
                  placeholder="Your name"
                  className="mt-2 w-full rounded-xl border border-line bg-white/5 px-4 py-3.5 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand transition" />
              </div>
              <div>
                <label className="label-eyebrow text-mutedText">Client</label>
                <input data-testid="welcome-client-input" value={clientName} onChange={(e) => setClientName(e.target.value)}
                  placeholder={clientType === "investor" ? "Who are we investing with?" : "Who are we building with?"}
                  className="mt-2 w-full rounded-xl border border-line bg-white/5 px-4 py-3.5 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand transition" />
              </div>
            </div>

            <button data-testid="welcome-start-btn" onClick={begin} disabled={loading}
              className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 text-white font-medium tracking-wide shadow-xl shadow-brand/25 transition-all hover:bg-brand-dark disabled:opacity-50">
              {loading ? "Starting…" : theme.button}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </div>
    </StageFrame>
  );
}
